import { useState, useEffect } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faEnvelope,
  faSms,
  faSave,
  faClock,
  faCalendar,
  faCheckCircle,
  faEdit,
  faTrash,
  faPlus,
  faUser,
  faPhone,
  faCalendarPlus,
  faChartLine,
  faDollarSign,
  faShoppingCart,
  faBox,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

// ─── FREQUENCY OPTIONS ──────────────────────────────────────────
const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily", icon: faClock },
  { value: "weekly", label: "Weekly", icon: faCalendar },
  { value: "monthly", label: "Monthly", icon: faCalendarPlus },
];

const NOTIFICATION_TYPES = [
  {
    id: "sales_summary",
    label: "Sales Summary",
    description: "Get summary of daily/weekly/monthly sales",
    icon: faChartLine,
  },
  {
    id: "profit_report",
    label: "Profit & Loss Report",
    description: "Receive profit and loss reports",
    icon: faDollarSign,
  },
  {
    id: "stock_alert",
    label: "Low Stock Alert",
    description: "Get notified when stock is running low",
    icon: faBox,
  },
  {
    id: "order_notification",
    label: "New Order Notification",
    description: "Receive alerts for new orders",
    icon: faShoppingCart,
  },
  {
    id: "customer_activity",
    label: "Customer Activity",
    description: "Get updates on customer activity",
    icon: faUsers,
  },
];

// ─── DEFAULT SETTINGS ──────────────────────────────────────────
const DEFAULT_SETTINGS = {
  emailEnabled: true,
  smsEnabled: false,
  emailAddress: "",
  phoneNumber: "",
  frequency: "daily",
  notificationTypes: NOTIFICATION_TYPES.map((t) => ({ ...t, enabled: true })),
  recipients: [
    {
      id: 1,
      name: "Hassan Salim Juma",
      email: "hassan@example.com",
      phone: "+255777123456",
      role: "Owner",
    },
    {
      id: 2,
      name: "Maryam Ali Kondo",
      email: "maryam@example.com",
      phone: "+255778234567",
      role: "Manager",
    },
  ],
};

// ─── MAIN COMPONENT ────────────────────────────────────────────
const NotificationSettingsTab = ({ shop, onUpdateShop, showToast }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saved, setSaved] = useState(false);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Staff",
  });
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    if (shop?.notificationSettings) {
      // Merge shop settings with defaults to ensure all fields exist
      setSettings({
        ...DEFAULT_SETTINGS,
        ...shop.notificationSettings,
        notificationTypes:
          shop.notificationSettings.notificationTypes ||
          DEFAULT_SETTINGS.notificationTypes,
        recipients:
          shop.notificationSettings.recipients || DEFAULT_SETTINGS.recipients,
      });
    }
  }, [shop]);

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFrequencyChange = (frequency) => {
    setSettings((prev) => ({ ...prev, frequency }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleRecipientInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecipient((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRecipient = () => {
    if (!newRecipient.name || !newRecipient.email) {
      showToast("Please fill in name and email", "error");
      return;
    }
    const updatedRecipients = [
      ...settings.recipients,
      { ...newRecipient, id: Date.now() },
    ];
    setSettings((prev) => ({ ...prev, recipients: updatedRecipients }));
    setNewRecipient({ name: "", email: "", phone: "", role: "Staff" });
    setShowAddRecipient(false);
    showToast("Recipient added successfully");
  };

  const handleRemoveRecipient = (id) => {
    if (window.confirm("Remove this recipient?")) {
      const updatedRecipients = settings.recipients.filter((r) => r.id !== id);
      setSettings((prev) => ({ ...prev, recipients: updatedRecipients }));
      showToast("Recipient removed");
    }
  };

  const handleToggleNotificationType = (id) => {
    setSettings((prev) => ({
      ...prev,
      notificationTypes: prev.notificationTypes.map((t) =>
        t.id === id ? { ...t, enabled: !t.enabled } : t,
      ),
    }));
  };

  const handleSaveSettings = () => {
    if (settings.emailEnabled && !settings.emailAddress) {
      showToast("Please enter email address", "error");
      return;
    }
    if (settings.smsEnabled && !settings.phoneNumber) {
      showToast("Please enter phone number", "error");
      return;
    }

    onUpdateShop({
      ...shop,
      notificationSettings: settings,
    });
    setSaved(true);
    showToast("Notification settings saved successfully");
    setTimeout(() => setSaved(false), 3000);
  };

  const getRecipientsByRole = (role) => {
    return settings.recipients.filter((r) => r.role === role).length;
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Notification Settings
          </h3>
          <p className="text-sm text-gray-600">
            Configure how and when you receive notifications
          </p>
        </div>
        <Button
          onClick={handleSaveSettings}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white shadow-sm"
        >
          <FontAwesomeIcon icon={faSave} className="h-4 w-4" />
          {saved ? "Saved!" : "Save Settings"}
        </Button>
      </div>

      {/* ─── Tabs ─── */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
        <button
          onClick={() => setActiveTab("general")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            activeTab === "general"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faBell} className="mr-2 h-3.5 w-3.5" />
          General Settings
        </button>
        <button
          onClick={() => setActiveTab("recipients")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            activeTab === "recipients"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faUsers} className="mr-2 h-3.5 w-3.5" />
          Recipients ({settings.recipients.length})
        </button>
        <button
          onClick={() => setActiveTab("types")}
          className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
            activeTab === "types"
              ? "bg-blue-800 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
          }`}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2 h-3.5 w-3.5" />
          Notification Types
        </button>
      </div>

      {/* ─── General Settings Tab ─── */}
      {activeTab === "general" && (
        <div className="space-y-4">
          {/* Notification Method */}
          <Card className="p-6 border border-gray-200 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faEnvelope}
                className="text-blue-600 h-4 w-4"
              />
              Notification Methods
            </h4>
            <div className="space-y-4">
              {/* Email Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-blue-600 h-4 w-4"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      Email Notifications
                    </p>
                    <p className="text-sm text-gray-600">
                      Receive notifications via email
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 sm:mt-0">
                  {settings.emailEnabled && (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-1 h-3 w-3"
                      />
                      Enabled
                    </Badge>
                  )}
                  <button
                    onClick={() => handleToggle("emailEnabled")}
                    className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                      settings.emailEnabled ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                        settings.emailEnabled
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {settings.emailEnabled && (
                <div className="ml-14">
                  <label className="text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <Input
                    name="emailAddress"
                    type="email"
                    placeholder="Enter email address"
                    value={settings.emailAddress}
                    onChange={handleInputChange}
                    className="mt-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Enter the email address where notifications will be sent
                  </p>
                </div>
              )}

              {/* SMS Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faSms}
                      className="text-green-600 h-4 w-4"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      SMS Notifications
                    </p>
                    <p className="text-sm text-gray-600">
                      Receive notifications via SMS
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 sm:mt-0">
                  {settings.smsEnabled && (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="mr-1 h-3 w-3"
                      />
                      Enabled
                    </Badge>
                  )}
                  <button
                    onClick={() => handleToggle("smsEnabled")}
                    className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                      settings.smsEnabled ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                        settings.smsEnabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              {settings.smsEnabled && (
                <div className="ml-14">
                  <label className="text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <Input
                    name="phoneNumber"
                    type="tel"
                    placeholder="Enter phone number (+255777123456)"
                    value={settings.phoneNumber}
                    onChange={handleInputChange}
                    className="mt-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-800/30"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Enter the phone number where SMS notifications will be sent
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Notification Frequency */}
          <Card className="p-6 border border-gray-200 shadow-sm">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faClock}
                className="text-blue-600 h-4 w-4"
              />
              Notification Frequency
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FREQUENCY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFrequencyChange(option.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 text-center ${
                    settings.frequency === option.value
                      ? "border-blue-800 bg-blue-50 shadow-sm"
                      : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={option.icon}
                    className={`h-6 w-6 mx-auto mb-2 ${
                      settings.frequency === option.value
                        ? "text-blue-600"
                        : "text-gray-600"
                    }`}
                  />
                  <p
                    className={`font-medium ${
                      settings.frequency === option.value
                        ? "text-blue-800"
                        : "text-gray-700"
                    }`}
                  >
                    {option.label}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {option.value === "daily" && "Get updates every day"}
                    {option.value === "weekly" && "Get updates every week"}
                    {option.value === "monthly" && "Get updates every month"}
                  </p>
                </button>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* ─── Recipients Tab ─── */}
      {activeTab === "recipients" && (
        <Card className="p-6 border border-gray-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faUsers}
                  className="text-blue-600 h-4 w-4"
                />
                Notification Recipients
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Manage who receives notifications
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAddRecipient(!showAddRecipient)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
              Add Recipient
            </Button>
          </div>

          {/* Add Recipient Form */}
          {showAddRecipient && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Name *
                  </label>
                  <Input
                    name="name"
                    placeholder="Full name"
                    value={newRecipient.name}
                    onChange={handleRecipientInputChange}
                    className="mt-1 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email address"
                    value={newRecipient.email}
                    onChange={handleRecipientInputChange}
                    className="mt-1 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <Input
                    name="phone"
                    placeholder="Phone number"
                    value={newRecipient.phone}
                    onChange={handleRecipientInputChange}
                    className="mt-1 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    name="role"
                    className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-800/30"
                    value={newRecipient.role}
                    onChange={handleRecipientInputChange}
                  >
                    <option value="Owner">Owner</option>
                    <option value="Manager">Manager</option>
                    <option value="Staff">Staff</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddRecipient(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddRecipient}
                  className="bg-blue-800 hover:bg-blue-900 text-white"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-1 h-3 w-3" />
                  Add Recipient
                </Button>
              </div>
            </div>
          )}

          {/* Recipients Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                    Name
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="mr-1 h-3 w-3"
                    />
                    Email
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                    <FontAwesomeIcon icon={faPhone} className="mr-1 h-3 w-3" />
                    Phone
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3">
                    Role
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 text-center">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.recipients.map((recipient, idx) => (
                  <TableRow
                    key={recipient.id}
                    className="hover:bg-blue-50/30 transition-colors border-b border-gray-100"
                  >
                    <TableCell className="py-2.5 px-3 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faUser}
                            className="text-blue-600 h-3 w-3"
                          />
                        </div>
                        {recipient.name}
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {recipient.email}
                    </TableCell>
                    <TableCell className="py-2.5 px-3 text-sm text-gray-600">
                      {recipient.phone || "-"}
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <Badge
                        className={`
                        ${
                          recipient.role === "Owner"
                            ? "bg-purple-100 text-purple-700 border-purple-200"
                            : recipient.role === "Manager"
                              ? "bg-blue-100 text-blue-700 border-blue-200"
                              : "bg-gray-100 text-gray-700 border-gray-200"
                        }
                      `}
                      >
                        {recipient.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2.5 px-3">
                      <div className="flex gap-1.5 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 border-gray-300 hover:border-blue-800 h-8 px-2.5 text-xs"
                        >
                          <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />{" "}
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveRecipient(recipient.id)}
                          className="gap-1 border-red-300 text-red-600 hover:bg-red-50 h-8 px-2.5 text-xs"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />{" "}
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {settings.recipients.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 text-gray-600"
                    >
                      No recipients added. Click "Add Recipient" to get started.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Recipients Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-xs text-gray-600">Owners</p>
              <p className="text-lg font-bold text-purple-600">
                {getRecipientsByRole("Owner")}
              </p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-gray-600">Managers</p>
              <p className="text-lg font-bold text-blue-600">
                {getRecipientsByRole("Manager")}
              </p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs text-gray-600">Staff</p>
              <p className="text-lg font-bold text-gray-600">
                {getRecipientsByRole("Staff")}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ─── Notification Types Tab ─── */}
      {activeTab === "types" && (
        <Card className="p-6 border border-gray-200 shadow-sm">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-blue-600 h-4 w-4"
            />
            Notification Types
          </h4>
          <p className="text-sm text-gray-600 mb-4">
            Select which types of notifications you want to receive
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {settings.notificationTypes.map((type) => (
              <div
                key={type.id}
                className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                  type.enabled
                    ? "border-blue-200 bg-blue-50/50"
                    : "border-gray-200 bg-gray-50/50 hover:border-gray-300"
                }`}
                onClick={() => handleToggleNotificationType(type.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        type.enabled ? "bg-blue-100" : "bg-gray-100"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={type.icon}
                        className={
                          type.enabled ? "text-blue-600" : "text-gray-600"
                        }
                      />
                    </div>
                    <div>
                      <p
                        className={`font-medium ${type.enabled ? "text-gray-900" : "text-gray-600"}`}
                      >
                        {type.label}
                      </p>
                      <p
                        className={`text-sm ${type.enabled ? "text-gray-600" : "text-gray-600"}`}
                      >
                        {type.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {type.enabled && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-green-500 h-5 w-5"
                      />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleNotificationType(type.id);
                      }}
                      className={`relative inline-flex items-center h-5 rounded-full w-9 transition-colors ${
                        type.enabled ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block w-3.5 h-3.5 transform bg-white rounded-full transition-transform ${
                          type.enabled ? "translate-x-5" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ─── Summary Card ─── */}
      <Card className="p-4 border border-gray-200 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBell}
                className="text-blue-600 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Notification Summary
              </p>
              <p className="text-xs text-gray-600">
                {settings.emailEnabled && settings.smsEnabled
                  ? "Email & SMS"
                  : settings.emailEnabled
                    ? "Email"
                    : settings.smsEnabled
                      ? "SMS"
                      : "No notifications enabled"}
                {" · "}
                {
                  FREQUENCY_OPTIONS.find((f) => f.value === settings.frequency)
                    ?.label
                }
                {" · "}
                {settings.recipients.length} recipient
                {settings.recipients.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {settings.emailEnabled && (
              <Badge className="bg-blue-800 text-white border-blue-200">
                <FontAwesomeIcon icon={faEnvelope} className="mr-1 h-3 w-3" />
                Email
              </Badge>
            )}
            {settings.smsEnabled && (
              <Badge className="bg-green-600 text-white border-green-200">
                <FontAwesomeIcon icon={faSms} className="mr-1 h-3 w-3" />
                SMS
              </Badge>
            )}
            <Badge className="bg-purple-700 text-white border-purple-200">
              <FontAwesomeIcon icon={faClock} className="mr-1 h-3 w-3" />
              {
                FREQUENCY_OPTIONS.find((f) => f.value === settings.frequency)
                  ?.label
              }
            </Badge>
            <Badge className="bg-orange-500 text-white border-orange-200">
              <FontAwesomeIcon icon={faUsers} className="mr-1 h-3 w-3" />
              {settings.recipients.length} recipients
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default NotificationSettingsTab;
